import { useEffect, useState, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import useStore from './store'

import Shell          from './components/layout/Shell'
import AuthPage       from './pages/AuthPage'
import OnboardingPage from './pages/OnboardingPage'
import CanvasPage     from './pages/CanvasPage'
import GoalsPage      from './pages/GoalsPage'
import GoalDetailPage from './pages/GoalDetailPage'
import ReviewPage     from './pages/ReviewPage'
import AnalysisPage   from './pages/AnalysisPage'
import ReEntry        from './components/shared/ReEntry'

export default function App() {
  const boot        = useStore(s => s.boot)
  const authReady   = useStore(s => s.authReady)
  const user        = useStore(s => s.user)
  const profile     = useStore(s => s.profile)
  const isOnboarded = useStore(s => s.isOnboarded)

  const [booted,      setBooted]      = useState(false)
  const [showReEntry, setShowReEntry] = useState(false)

  // Boot exactly once — ref guard prevents double-invocation in StrictMode
  const bootedRef = useRef(false)
  useEffect(() => {
    if (bootedRef.current) return
    bootedRef.current = true
    boot().then(() => setBooted(true))
  }, [boot])

  // Inactivity check — runs after boot + profile is available
  useEffect(() => {
    if (!booted || !user || !profile || !isOnboarded()) return
    const last = profile.last_session_at
    if (!last) return
    const daysSince = (Date.now() - new Date(last)) / 86400000
    if (daysSince >= 14) setShowReEntry(true)
  }, [booted, user, profile, isOnboarded])

  // Loading splash
  if (!authReady || !booted) {
    return (
      <div className="min-h-screen bg-m-bg flex flex-col items-center justify-center gap-3">
        <span className="font-mono text-sm tracking-[0.3em] uppercase text-m-accent">
          MORI
        </span>
        <span className="font-mono text-[10px] text-m-faint animate-pulse-slow">
          loading…
        </span>
      </div>
    )
  }

  // Schema not yet run — show helpful prompt instead of crashing
  if (user && !profile && isOnboarded !== undefined) {
    const isNew = !profile
    if (isNew) {
      // Profile table may not exist — redirect to onboarding which will
      // create the profile row via upsert
    }
  }

  // Re-entry
  if (showReEntry) return <ReEntry onDismiss={() => setShowReEntry(false)} />

  // Not authenticated
  if (!user) {
    return (
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="*"     element={<Navigate to="/auth" replace />} />
      </Routes>
    )
  }

  // Authenticated but not onboarded
  if (!isOnboarded()) {
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="*"           element={<Navigate to="/onboarding" replace />} />
      </Routes>
    )
  }

  // Full app
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/"          element={<Navigate to="/canvas" replace />} />
        <Route path="/canvas"    element={<CanvasPage />} />
        <Route path="/goals"     element={<GoalsPage />} />
        <Route path="/goals/:id" element={<GoalDetailPage />} />
        <Route path="/review"    element={<ReviewPage />} />
        <Route path="/analysis"  element={<AnalysisPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/canvas" replace />} />
    </Routes>
  )
}
